import { useEffect, useState } from "react";
import { Alert, Badge, Button, Collapse, Form, Image, Modal, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import {
	mdiChevronDown,
	mdiChevronRight,
	mdiEyeOutline,
	mdiImagePlusOutline,
	mdiMinusThick,
	mdiPencilOutline,
	mdiSort,
	mdiTrashCanOutline,
} from "@mdi/js";

import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { QuestionnaireResponseQuestionFile } from "src/entities/QuestionnaireResponseEntity";
import { DEFAULT_CRITERIA_MANIFESTATION_IMAGE_CAPTION_LIMIT } from "src/config/criteria";

export type ManifestationImageSelections = Record<number, QuestionnaireResponseQuestionFile[]>;

type ImagesStepProps = {
	manifestations: CriteriaManifestationEntity[];
	files: ManifestationImageSelections;
	maxImages: number;
	uploadingImages: Record<string, boolean>;
	uploadError: string | null;
	onChange: (files: ManifestationImageSelections) => void;
	onCanAdvanceChange: (canAdvance: boolean) => void;
};

type SelectedImage = {
	manifestationId: number;
	fileIndex: number;
};

type OrderImagesModal = {
	manifestationId: number;
	files: QuestionnaireResponseQuestionFile[];
};

type SortableImageListProps = {
	items: QuestionnaireResponseQuestionFile[];
	onSortEnd: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
};

const SortableImageItem = SortableElement<{ item: QuestionnaireResponseQuestionFile }>(({ item }: any) => {
	return (
		<li className="d-flex align-items-center gap-3 border-bottom p-2 sortable-item list-unstyled" style={{ zIndex: 1060, cursor: "ns-resize" }}>
			<Icon path={mdiMinusThick} size={0.8} className="flex-shrink-0" />
			<div className="border rounded-3 overflow-hidden bg-light d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 56, height: 40 }}>
				{getFileSource(item) ? (
					<Image src={getFileSource(item)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
				) : (
					<Icon path={mdiImagePlusOutline} size={0.9} />
				)}
			</div>
			<div style={{ minWidth: 0, flexBasis: 0, flexGrow: 1 }}>
				<div className="fw-medium text-truncate">{item.fileName}</div>
				<div className="small text-muted text-truncate">{item.caption ? `Legenda: ${item.caption}` : "Sem legenda"}</div>
			</div>
		</li>
	);
});

const SortableImageList = SortableContainer<SortableImageListProps>(({ items }: any) => {
	return (
		<ul className="border rounded-3 list-unstyled user-select-none mb-0" style={{ cursor: "ns-resize" }}>
			{items.map((value: QuestionnaireResponseQuestionFile, index: number) => (
				<SortableImageItem key={`image-${index}`} index={index} item={value} />
			))}
		</ul>
	);
});

function getFileSource(file: QuestionnaireResponseQuestionFile | undefined) {
	return file?.data ?? file?.fileUrl ?? "";
}

function getUploadKey(manifestationId: number, fileIndex: number) {
	return `${manifestationId}:${fileIndex}`;
}

function applyImageOrder(files: QuestionnaireResponseQuestionFile[]) {
	return files.map((file, index) => ({ ...file, order: index }));
}

export default function ImagesStep({
	manifestations,
	files,
	maxImages,
	uploadingImages,
	uploadError,
	onChange,
	onCanAdvanceChange,
}: ImagesStepProps) {
	const [expandedManifestations, setExpandedManifestations] = useState<Set<number>>(() => new Set(manifestations[0] ? [manifestations[0].id] : []));
	const [editImage, setEditImage] = useState<SelectedImage | null>(null);
	const [previewImage, setPreviewImage] = useState<SelectedImage | null>(null);
	const [orderImagesModal, setOrderImagesModal] = useState<OrderImagesModal | null>(null);

	useEffect(() => {
		setExpandedManifestations(new Set(manifestations[0] ? [manifestations[0].id] : []));
	}, [manifestations]);

	useEffect(() => {
		onCanAdvanceChange(true);
	}, [onCanAdvanceChange]);

	function handleToggleManifestation(manifestationId: number) {
		setExpandedManifestations((current) => {
			const next = new Set(current);
			if (next.has(manifestationId)) {
				next.delete(manifestationId);
			} else {
				next.add(manifestationId);
			}
			return next;
		});
	}

	function handleFileChange(manifestationId: number, event: any, fileIndex?: number) {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) {
			return;
		}

		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onloadend = () => {
			if (typeof reader.result !== "string") {
				return;
			}

			const manifestationFiles = files[manifestationId] ?? [];
			const nextIndex = fileIndex ?? manifestationFiles.length;
			const previousCaption = fileIndex !== undefined ? manifestationFiles[fileIndex]?.caption : "";
			const previousOrder = fileIndex !== undefined ? manifestationFiles[fileIndex]?.order : nextIndex;
			const nextFile: QuestionnaireResponseQuestionFile = {
				fileName: file.name,
				contentType: file.type,
				data: reader.result,
				blob: file,
				size: file.size,
				caption: previousCaption ?? "",
				order: previousOrder,
			};

			const nextManifestationFiles =
				fileIndex === undefined
					? [...manifestationFiles, nextFile]
					: manifestationFiles.map((currentFile, index) => (index === fileIndex ? nextFile : currentFile));

			onChange({
				...files,
				[manifestationId]: applyImageOrder(nextManifestationFiles),
			});
			setEditImage({ manifestationId, fileIndex: nextIndex });
		};
	}

	function handleCaptionChange(manifestationId: number, fileIndex: number, caption: string) {
		onChange({
			...files,
			[manifestationId]: (files[manifestationId] ?? []).map((file, index) => (index === fileIndex ? { ...file, caption } : file)),
		});
	}

	function handleRemove(manifestationId: number, fileIndex: number) {
		onChange({
			...files,
			[manifestationId]: applyImageOrder((files[manifestationId] ?? []).filter((_, index) => index !== fileIndex)),
		});
	}

	function handleOpenOrderModal(manifestationId: number) {
		setOrderImagesModal({
			manifestationId,
			files: applyImageOrder([...(files[manifestationId] ?? [])]),
		});
	}

	function handleOrderSave() {
		if (!orderImagesModal) {
			return;
		}

		onChange({
			...files,
			[orderImagesModal.manifestationId]: applyImageOrder(orderImagesModal.files),
		});
		setOrderImagesModal(null);
	}

	const editFile = editImage ? files[editImage.manifestationId]?.[editImage.fileIndex] : undefined;
	const editManifestation = editImage ? manifestations.find((manifestation) => manifestation.id === editImage.manifestationId) : undefined;
	const previewFile = previewImage ? files[previewImage.manifestationId]?.[previewImage.fileIndex] : undefined;
	const previewManifestation = previewImage ? manifestations.find((manifestation) => manifestation.id === previewImage.manifestationId) : undefined;
	const orderManifestation = orderImagesModal ? manifestations.find((manifestation) => manifestation.id === orderImagesModal.manifestationId) : undefined;

	if (manifestations.length === 0) {
		return <Alert variant="light">Selecione ao menos uma manifestação para anexar imagens.</Alert>;
	}

	return (
		<>
			<div className="d-flex flex-column gap-2 w-100">
				{uploadError && <Alert variant="danger">{uploadError}</Alert>}
				{manifestations.map((manifestation) => {
					const expanded = expandedManifestations.has(manifestation.id);
					const manifestationFiles = files[manifestation.id] ?? [];
					const canAddImage = manifestationFiles.length < maxImages;

					return (
						<div key={manifestation.id}>
							<Button
								type="button"
								variant="light"
								className="d-flex align-items-center justify-content-between rounded-3 border px-3 fw-medium w-100 text-start"
								style={{ minHeight: 40 }}
								onClick={() => handleToggleManifestation(manifestation.id)}
								aria-expanded={expanded}
							>
								<span>{manifestation.title}</span>
								<span className="d-flex align-items-center gap-2">
									<Badge bg="light" text="dark" className="border fw-normal">
										{manifestationFiles.length}/{maxImages}
									</Badge>
									<Icon path={expanded ? mdiChevronDown : mdiChevronRight} size={0.8} />
								</span>
							</Button>

							<Collapse in={expanded}>
								<div>
									<div className="d-flex flex-column gap-2 my-3 ms-4">
										{manifestationFiles.map((file, fileIndex) => {
												const uploadKey = getUploadKey(manifestation.id, fileIndex);
												const uploading = uploadingImages[uploadKey] === true;

												return (
													<div key={fileIndex} className="rounded-3 border p-2 d-flex flex-column flex-md-row gap-3 align-items-md-center">
														<div className="d-flex align-items-center gap-3 flex-fill" style={{ minWidth: 0 }}>
															<div className="border rounded-3 overflow-hidden bg-light d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 72, height: 48 }}>
																{uploading ? (
																	<Spinner size="sm" />
																) : getFileSource(file) ? (
																	<Image src={getFileSource(file)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
																) : (
																	<Icon path={mdiImagePlusOutline} size={1} />
																)}
															</div>
															<div style={{ minWidth: 0 }}>
																<div className="fw-medium text-truncate">{file.fileName}</div>
																<div className="small text-muted text-truncate">
																	{file.caption ? `Legenda: ${file.caption}` : "Sem legenda"}
																</div>
															</div>
														</div>
														<div className="d-flex align-items-center gap-2 flex-wrap">
															<Button type="button" variant="light" className="border rounded-circle p-2 d-flex" onClick={() => setPreviewImage({ manifestationId: manifestation.id, fileIndex })}>
																<Icon path={mdiEyeOutline} size={0.8} />
															</Button>
															<Button type="button" variant="light" className="border rounded-circle p-2 d-flex" onClick={() => setEditImage({ manifestationId: manifestation.id, fileIndex })}>
																<Icon path={mdiPencilOutline} size={0.8} />
															</Button>
															<Button type="button" variant="light" className="border rounded-circle p-2 d-flex" onClick={() => handleRemove(manifestation.id, fileIndex)}>
																<Icon path={mdiTrashCanOutline} size={0.8} />
															</Button>
														</div>
													</div>
												);
											})}

										<div className="d-flex align-items-center gap-2 flex-wrap">
											{canAddImage && (
												<Form.Label className="mb-0">
													<Button as="span" type="button" variant="light" className="border rounded-3 d-inline-flex align-items-center gap-2">
														<Icon path={mdiImagePlusOutline} size={0.9} />
														Adicionar imagem
													</Button>
													<Form.Control className="d-none" type="file" accept="image/*" onChange={(event) => handleFileChange(manifestation.id, event)} />
												</Form.Label>
											)}
											<Button
												type="button"
												variant="light"
												className="border rounded-3 d-inline-flex align-items-center gap-2"
												onClick={() => handleOpenOrderModal(manifestation.id)}
												disabled={manifestationFiles.length < 2}
											>
												<Icon path={mdiSort} size={0.9} />
												Ordenar
											</Button>
										</div>
									</div>
								</div>
							</Collapse>
						</div>
					);
				})}
			</div>

			<Modal show={Boolean(editFile)} onHide={() => setEditImage(null)} centered>
				<Modal.Header closeButton>
					<Modal.Title>{editManifestation?.title ?? "Legenda"}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{editFile && editImage && (
						<Form.Group>
							<Form.Label>
								Legenda{" "}
								<small className="text-muted">
									({(editFile.caption ?? "").length}/{DEFAULT_CRITERIA_MANIFESTATION_IMAGE_CAPTION_LIMIT})
								</small>
							</Form.Label>
							<Form.Control
								as="textarea"
								rows={3}
								maxLength={DEFAULT_CRITERIA_MANIFESTATION_IMAGE_CAPTION_LIMIT}
								placeholder="Informe uma legenda para a imagem"
								value={editFile.caption ?? ""}
								onChange={(event) => handleCaptionChange(editImage.manifestationId, editImage.fileIndex, event.target.value)}
							/>
						</Form.Group>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button type="button" onClick={() => setEditImage(null)}>
						Concluir
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal show={Boolean(previewFile)} onHide={() => setPreviewImage(null)} centered size="lg">
				<Modal.Header closeButton>
					<Modal.Title>{previewManifestation?.title ?? "Imagem"}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{previewFile && (
						<div className="d-flex flex-column gap-3">
							<div className="border rounded-3 bg-light d-flex align-items-center justify-content-center overflow-hidden" style={{ minHeight: 360 }}>
								{getFileSource(previewFile) ? <Image src={getFileSource(previewFile)} fluid /> : <Icon path={mdiImagePlusOutline} size={2} />}
							</div>
							<div>
								<div className="fw-medium text-break">{previewFile.fileName}</div>
								<div className="small text-muted text-break">
									{previewFile.caption ? `Legenda: ${previewFile.caption}` : "Sem legenda"}
								</div>
							</div>
						</div>
					)}
				</Modal.Body>
			</Modal>

			<Modal show={Boolean(orderImagesModal)} onHide={() => setOrderImagesModal(null)} centered size="lg">
				<Modal.Header closeButton>
					<Modal.Title className="d-flex flex-column lh-1">
						<div className="fs-6">
							<small className="text-muted fw-light">Ordenar Imagens</small>
						</div>
						{orderManifestation?.title ?? "Manifestação"}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{orderImagesModal && (
						<SortableImageList
							items={orderImagesModal.files}
							onSortEnd={(values) => {
								const orderedFiles = [...orderImagesModal.files];
								const [file] = orderedFiles.splice(values.oldIndex, 1);
								orderedFiles.splice(values.newIndex, 0, file);
								setOrderImagesModal({ ...orderImagesModal, files: applyImageOrder(orderedFiles) });
							}}
							lockAxis="y"
							helperClass="sortable-item--dragging"
						/>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button type="button" variant="secondary" onClick={() => setOrderImagesModal(null)}>
						Cancelar
					</Button>
					<Button type="button" onClick={handleOrderSave}>
						Salvar
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}
